/* When to cut a recording into a chunk for Whisper.

   Pure, so the timing can be tested without a microphone. The recorder is
   STOPPED and restarted at each cut rather than sliced: a slice of a running
   WebM stream has no header and does not decode on its own, which would come
   back from Whisper as silence.

   A cut on a pause sends the phrase. A cut on the time limit sends it too,
   mid-sentence if need be, because a 20-second chunk keeps the lag between
   speaking and seeing text bounded and the upload small. A chunk in which
   nobody spoke is discarded, not sent — Whisper given silence tends to invent
   a stock phrase.

   "Quiet" is relative. A laptop microphone with automatic gain keeps the room
   at a level a fixed threshold reads as speech, so with one the first pilot
   never saw a pause and nothing appeared until the mic was stopped. The
   chunker therefore keeps a NOISE FLOOR — what the room sounds like between
   words — and treats speech as anything clearly above it. The floor falls
   fast and rises slowly, so a dip between words pulls it down at once while
   a long sentence barely moves it, and it is carried from chunk to chunk
   because the room does not change at a cut. */

export interface ChunkConfig {
  /** Silence after speech that ends a phrase. Long enough for a breath, short enough that text appears promptly. */
  silenceMs: number;
  /** Longest chunk regardless of pauses. */
  maxMs: number;
  /** Never cut a chunk shorter than this; a very short blob is more header than audio. */
  minMs: number;
  /** The least level (0..1 RMS of byte samples) that can ever count as speech, however quiet the room. */
  threshold: number;
  /** Speech is at least this many times the noise floor. */
  floorRatio: number;
  /** After the mic opens, the floor follows the level straight up for this long: nobody has started talking yet. */
  calibrateMs: number;
}

export const CHUNK: ChunkConfig = { silenceMs: 1200, maxMs: 20_000, minMs: 600, threshold: 0.012, floorRatio: 2.5, calibrateMs: 400 };

export interface ChunkState {
  startedAt: number;
  lastSpeechAt: number;
  speech: boolean;
  /** The room between words. */
  floor: number;
  /** Until when the floor simply follows the level (first chunk only). */
  calibrateUntil: number;
  /** The latest sample, for a meter. */
  level: number;
}

/** A new chunk. Pass the previous chunk's floor so the room is not re-learned at every cut. */
export function chunkStart(now: number, floor?: number, cfg: ChunkConfig = CHUNK): ChunkState {
  return {
    startedAt: now, lastSpeechAt: now, speech: false, level: 0,
    floor: floor ?? cfg.threshold / 2,
    calibrateUntil: floor === undefined ? now + cfg.calibrateMs : 0,
  };
}

/** What counts as speech right now. */
export const speechGate = (s: ChunkState, cfg: ChunkConfig = CHUNK): number => Math.max(cfg.threshold, s.floor * cfg.floorRatio);

/** 0..1 for a meter: 0 at the floor, 1 at a confident speaking level. */
export function voiceLevel(s: ChunkState, cfg: ChunkConfig = CHUNK): number {
  const gate = speechGate(s, cfg);
  return Math.max(0, Math.min(1, (s.level - s.floor) / (gate * 2)));
}

/** One level sample. `cut` says what to do with the chunk so far: send it, discard it, or keep recording. */
export function chunkStep(s: ChunkState, level: number, now: number, cfg: ChunkConfig = CHUNK): { state: ChunkState; cut: 'send' | 'discard' | null } {
  const gate = speechGate(s, cfg);
  const speaking = level >= gate;
  let floor: number;
  if (now < s.calibrateUntil) floor = Math.max(s.floor, level);
  else if (level < s.floor) floor = s.floor + (level - s.floor) * 0.3;        // falls fast: a dip between words is the floor
  else floor = s.floor + (level - s.floor) * (speaking ? 0.0002 : 0.02);    // rises slowly, and through speech hardly at all
  const state: ChunkState = { ...s, floor, level, ...(speaking ? { speech: true, lastSpeechAt: now } : {}) };
  const elapsed = now - state.startedAt;
  if (elapsed < cfg.minMs) return { state, cut: null };
  if (state.speech && now - state.lastSpeechAt >= cfg.silenceMs) return { state, cut: 'send' };
  if (elapsed >= cfg.maxMs) return { state, cut: state.speech ? 'send' : 'discard' };
  return { state, cut: null };
}

/** RMS of byte time-domain samples (0..255, silence at 128), as 0..1. */
export function rmsLevel(samples: ArrayLike<number>): number {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) { const v = (samples[i] - 128) / 128; sum += v * v; }
  return Math.sqrt(sum / samples.length);
}
